
import { AllCommunityModules } from '@ag-grid-community/all-modules';
import { ColumnOptions } from '../interfaces';
import { ColHeaderCellComponent } from '../components/cell-components/col-header-cell/col-header-cell.component';
import { BtnCellComponent } from '../components/cell-components/btn-cell/btn-cell.component';
import { LinkCellComponent } from '../components/cell-components/link-cell/link-cell.component';
import { SummarizeCellComponent } from '../components/cell-components/summarize-cell/summarize-cell.component';

export class BmGrid {

  static readonly modules = AllCommunityModules;
  static readonly frameworkComponents = {
    button: BtnCellComponent,
    link: LinkCellComponent,
    summarize: SummarizeCellComponent,
  };

  constructor(
    private data: any,
    private options: ColumnOptions[]) {}

  getRowData() {
    return this.data;
  }

  getColumnDef(colName: string) {
    const isHidden = this.isHidden(colName);
    const hasSummary = this.hasSummary(colName);
    const options = this.getColumnOptions(colName);

    const defaultDefs = {
      headerName: colName,
      field: colName,
      hide: isHidden,
      resizable: true,
      headerComponentFramework: ColHeaderCellComponent,
      pinnedRowCellRenderer: hasSummary ? 'summarize' : null,
    };

    if (!options) {
      return defaultDefs;
    }

    return Object.assign(defaultDefs, {
      cellRenderer: options.colType,
      cellStyle: (params) => {
        return this.parseRole(options.role, params.value);
      },
    });

  }

  getColumnDefs() {

    const sampleRow = this.data[0];
    if (!sampleRow) {
      return [];
    }

    return Object.keys(sampleRow).map(colName => this.getColumnDef(colName));
  }

  getSummarizeRowData() {
    if (!this.data.length) {
      return [];
    }

    const sampleRow = this.data[0];
    const initialState = {};
    const summarizeCols = this.getSummarizeColumns();

    if (summarizeCols.length === 0) {
      return [];
    }

    Object.keys(sampleRow).forEach(key => {
      initialState[key] = summarizeCols.includes(key) ? 0 : null;
    });

    const summarizeRowData = [this.data.reduce((acc, current) => {

      return Object.assign(
        {},
        initialState,
        ...summarizeCols.map(colName => {
          return { [colName]: (+acc[colName] + +current[colName]).toFixed(2) };
        })
      );

    }, initialState)];

    return summarizeRowData;
  }

  getHiddenColumns(): string[] {
    const hiddenColsOption = this.options.find(option => 'colToHide' in option);
    return hiddenColsOption ? hiddenColsOption.hideCols : [];
  }

  isHidden(colName: string): boolean {
    return this.getHiddenColumns().includes(colName);
  }

  getSummarizeColumns(): string[] {
    const summarizeColsOption = this.options.find(option => 'summarizeCols' in option);
    return summarizeColsOption ? summarizeColsOption.summarizeCols : [];
  }

  hasSummary(colName: string): boolean {
    return this.getSummarizeColumns().includes(colName);
  }

  parseRole(role: any[], value: any) {
    if (role) {
      const moreThenOption = role.find(roleOption => 'moreThen' in roleOption);
      const lessThenOption = role.find(roleOption => 'lessThen' in roleOption);
      return Object.assign({}, this.parseMoreThenRole(moreThenOption, value), this.parseLessThenRole(lessThenOption, value));
    }
    return {};
  }

  getColumnOptions(colName: string): ColumnOptions {
    return this.options.find(option => option.colName === colName);
  }

  getColumnsWithOptions() {
    return this.options.filter(option => 'colName' in option).map(option => option.colName);
  }

  private parseMoreThenRole(role: {moreThen: number, color: string}, value: number) {
    if (role && value >= role.moreThen) {
      return {color: role.color};
    }
    return {};
  }

  private parseLessThenRole(role: {lessThen: number, color: string}, value: number) {
    if (role && value < role.lessThen) {
      return {color: role.color};
    }
    return {};
  }

}
